import React from 'react';
import {
  Dialog,
  Typography,
  Box,
} from '@mui/material';
import {
  Storage as StorageIcon,
  Psychology as PsychologyIcon,
  TuneRounded as ControlIcon,
} from '@mui/icons-material';
import { StandardButton } from './design-system';

interface DataDisclosureDialogProps {
  open: boolean;
  onAccept: () => void;
  onDecline: () => void;
}

const bulletItems = [
  {
    icon: <StorageIcon sx={{ fontSize: 20, color: '#00E0FF', filter: 'drop-shadow(0 0 6px rgba(0,224,255,0.4))' }} />,
    text: 'We collect your nutrition, health and fitness data to give you your scores and suggestions.',
  },
  {
    icon: <PsychologyIcon sx={{ fontSize: 20, color: '#CE93D8', filter: 'drop-shadow(0 0 6px rgba(206,147,216,0.4))' }} />,
    text: 'Your data is sent to AI services from Google and OpenAI, which produce automated estimates and suggestions.',
  },
  {
    icon: <ControlIcon sx={{ fontSize: 20, color: '#00E676', filter: 'drop-shadow(0 0 6px rgba(0,230,118,0.4))' }} />,
    text: 'You control your data. See it, export it or delete it any time in Settings.',
  },
];

export const DataDisclosureDialog: React.FC<DataDisclosureDialogProps> = ({
  open,
  onAccept,
  onDecline,
}) => {
  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      onClose={() => {}}
      PaperProps={{
        sx: {
          background: 'var(--bg-card)',
          border: '1px solid rgba(0,224,255,0.15)',
          borderRadius: 3,
          maxWidth: 420,
          mx: 'auto',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '2px',
            background: 'linear-gradient(90deg, transparent, #00E0FF, transparent)',
          },
        },
      }}
    >
      <Box sx={{ p: 3.5, textAlign: 'center' }}>
        {/* Logo */}
        <img
          src="/FuelTron.png"
          alt="FuelTron"
          style={{ width: 64, height: 64, borderRadius: '50%', marginBottom: 12 }}
        />

        <Typography
          sx={{
            fontFamily: 'var(--font-mono)',
            fontSize: '1rem',
            fontWeight: 700,
            color: '#00E0FF',
            textTransform: 'uppercase',
            letterSpacing: 2,
            mb: 2.5,
          }}
        >
          Your data, your control
        </Typography>

        {/* Bullet points */}
        <Box sx={{ textAlign: 'left', mb: 3 }}>
          {bulletItems.map((item, i) => (
            <Box
              key={i}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.5,
                mb: i < bulletItems.length - 1 ? 2 : 0,
              }}
            >
              <Box sx={{ flexShrink: 0, mt: 0.2 }}>{item.icon}</Box>
              <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem', lineHeight: 1.5 }}>
                {item.text}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Buttons */}
        <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center' }}>
          <StandardButton
            variant="text"
            onClick={onDecline}
            sx={{
              color: 'rgba(255,255,255,0.6)',
              fontSize: '0.8rem',
              fontFamily: 'var(--font-mono)',
              '&:hover': { color: '#FF1744' },
            }}
          >
            Decline
          </StandardButton>
          <StandardButton
            variant="gradient"
            onClick={onAccept}
            sx={{
              px: 4,
              textTransform: 'none',
              fontSize: '0.9rem',
            }}
          >
            Accept and continue
          </StandardButton>
        </Box>

        {/* Medical disclaimer */}
        <Typography sx={{ color: 'rgba(255,215,64,0.35)', fontSize: '0.6rem', mt: 2, fontFamily: 'var(--font-mono)', lineHeight: 1.5 }}>
          FuelTron is not a medical device. It does not diagnose, treat, cure or prevent any disease. For information only.
        </Typography>

        {/* Fine print */}
        <Typography sx={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.7rem', mt: 1, fontFamily: 'var(--font-mono)' }}>
          Full details are in the Privacy Policy, in Settings.
        </Typography>
      </Box>
    </Dialog>
  );
};
